import { useEffect, useState } from "react";

const injectedConfiguration = (window as any).roadside;
const environmentConfiguration = process.env;

const mergedConfiguration = {
    ...environmentConfiguration,
    ...injectedConfiguration,
} as { [key: string]: any };

interface Configuration {
    roadsideApi: {
        baseUrl: string;
    };
    qas: {
        url: string;
        token: string;
        dataset: {
            postal: "PAF" | "GNAF" | "DataFusion";
            residential: "PAF" | "GNAF" | "DataFusion";
        };
    };
    recaptcha: {
        siteKey: string;
    };
    idleTimeoutInSeconds: number;
    datoCms: {
        key: string;
        preview: boolean;
        environment?: string;
    };
    appInsights: {
        instrumentationKey: string;
    };
}

const toBoolean = (value?: any) => {
    if (typeof value === "string") {
        return value === "true";
    }

    return Boolean(value);
};

const toEnvironment = (value?: any): string | undefined => {
    if (!value || typeof value !== "string" || value.length === 0) {
        return undefined;
    }

    return value;
};

const createConfig = (merged: { [p: string]: any }) => {
    // Validate that we have our expected ENV variables defined!
    // Should always match environment variables read just below
    [
        "REACT_APP_ROADSIDE_API_BASE_URL",
        "REACT_APP_ROADSIDE_QAS_URL",
        "REACT_APP_ROADSIDE_QAS_ENRICHMENT_URL",
        "REACT_APP_ROADSIDE_QAS_TOKEN",
        "REACT_APP_ROADSIDE_QAS_DATASET_POSTAL",
        "REACT_APP_ROADSIDE_QAS_DATASET_RESIDENTIAL",
        "REACT_APP_ROADSIDE_IDLE_TIMEOUT_IN_SECONDS",
        "REACT_APP_ROADSIDE_DATOCMS_PUBLIC_KEY",
        "REACT_APP_ROADSIDE_DATOCMS_ENVIRONMENT",
        "REACT_APP_APP_INSIGHTS_INSTRUMENTATION_KEY",
    ].forEach((variable) => {
        if (!merged[variable]) {
            throw new Error(`Environment is missing variable ${variable}`);
        }
    });

    if (!merged["REACT_APP_ROADSIDE_RECAPTCHA_SITE_KEY"]) {
        console.log("REACT_APP_ROADSIDE_RECAPTCHA_SITE_KEY not found. ReCAPTCHA disabled.");
    }

    return {
        roadsideApi: {
            baseUrl: merged["REACT_APP_ROADSIDE_API_BASE_URL"],
        },
        qas: {
            url: merged["REACT_APP_ROADSIDE_QAS_URL"],
            enrichmentUrl: merged["REACT_APP_ROADSIDE_QAS_ENRICHMENT_URL"],
            token: merged["REACT_APP_ROADSIDE_QAS_TOKEN"],
            dataset: {
                postal: merged["REACT_APP_ROADSIDE_QAS_DATASET_POSTAL"],
                residential: merged["REACT_APP_ROADSIDE_QAS_DATASET_RESIDENTIAL"],
            },
        },
        recaptcha: {
            siteKey: merged["REACT_APP_ROADSIDE_RECAPTCHA_SITE_KEY"],
        },
        idleTimeoutInSeconds: merged["REACT_APP_ROADSIDE_IDLE_TIMEOUT_IN_SECONDS"],
        datoCms: {
            key: merged["REACT_APP_ROADSIDE_DATOCMS_PUBLIC_KEY"],
            preview: toBoolean(merged["REACT_APP_ROADSIDE_DATOCMS_PREVIEW"]),
            environment: toEnvironment(merged["REACT_APP_ROADSIDE_DATOCMS_ENVIRONMENT"]),
        },
        appInsights: {
            instrumentationKey: merged["REACT_APP_APP_INSIGHTS_INSTRUMENTATION_KEY"],
        },
    };
};

const appConfiguration = createConfig(mergedConfiguration);

const isEqual = (a: any, b: any): boolean => {
    let equal = true;
    Object.keys(a || {})
        .concat(Object.keys(b || {}))
        .forEach((k) => {
            if (a[k] !== b[k]) {
                equal = false;
                return;
            }
        });
    return equal;
};

export const useConfiguration = (): Configuration => {
    const [configOnWindow, setConfigOnWindow] = useState({ ...(window as any).roadside });
    const [config, setConfig] = useState(createConfig({ ...environmentConfiguration, ...(window as any).roadside }));

    useEffect(() => {
        const timer = setInterval(() => {
            if (!isEqual((window as any).roadside, configOnWindow)) {
                setConfigOnWindow({ ...(window as any).roadside });
                setConfig(createConfig({ ...environmentConfiguration, ...(window as any).roadside }));
            }
        }, 1000);

        return () => {
            clearTimeout(timer);
        };
    }, [configOnWindow]);

    return config;
};

export default appConfiguration;
